import React, { createContext, useContext, useState, useCallback } from 'react';

interface DialogState {
  isOpen: boolean;
  type: 'confirm' | 'alert' | 'help';
  title?: string;
  message?: string;
  content?: string | React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  okText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  maxWidth?: string;
  maxHeight?: string;
}

interface DialogContextType {
  showConfirm: (options: {
    title?: string;
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => void;
  showAlert: (options: {
    title?: string;
    message: string;
    onClose?: () => void;
    okText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => void;
  showHelp: (options: {
    title?: string;
    content: string | React.ReactNode;
    onClose?: () => void;
    maxWidth?: string;
    maxHeight?: string;
  }) => void;
  closeDialog: () => void;
  dialogState: DialogState;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

interface DialogProviderProps {
  children: React.ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    type: 'confirm',
    message: '',
  });

  const closeDialog = useCallback(() => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showConfirm = useCallback((options: {
    title?: string;
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => {
    setDialogState({
      isOpen: true,
      type: 'confirm',
      title: options.title,
      message: options.message,
      onConfirm: options.onConfirm,
      onCancel: options.onCancel,
      confirmText: options.confirmText,
      cancelText: options.cancelText,
      variant: options.variant || 'info',
    });
  }, []);

  const showAlert = useCallback((options: {
    title?: string;
    message: string;
    onClose?: () => void;
    okText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => {
    setDialogState({
      isOpen: true,
      type: 'alert',
      title: options.title,
      message: options.message,
      onCancel: options.onClose,
      okText: options.okText,
      variant: options.variant || 'info',
    });
  }, []);

  const showHelp = useCallback((options: {
    title?: string;
    content: string | React.ReactNode;
    onClose?: () => void;
    maxWidth?: string;
    maxHeight?: string;
  }) => {
    setDialogState({
      isOpen: true,
      type: 'help',
      title: options.title,
      content: options.content,
      onCancel: options.onClose,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
    });
  }, []);

  const value: DialogContextType = {
    showConfirm,
    showAlert,
    showHelp,
    closeDialog,
    dialogState,
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
    </DialogContext.Provider>
  );
}; 